// LLM enrichment orchestrator. Composes prompts → output-validator → engine
// into the v1.0 enrichment pass. Runs *after* the pattern engine; emits a
// LlmEnrichmentResult that the side panel merges into the rendered state and
// persists into the IndexedDB SkimFindingRecord schema.
//
// Discipline:
//   - The pattern result is always available. LLM enrichment refines it.
//   - When the engine is absent / not loaded / errors out, this function
//     returns null (no enrichment), and the pattern result remains the source
//     of truth — fallback discipline per PRD §9.7.
//   - Each LLM call's prompt_version + model_id is recorded on the emitted
//     findings, ready to be written into SkimFindingRecord.modelVersion.

import type { LlmClient } from './types';
import { callWithSchema } from './output-validator';
import * as classifyStudy from './prompts/classify_study';
import * as judgeChecklistItem from './prompts/judge_checklist_item';
import * as classifySpin from './prompts/classify_spin';
import * as draftRationale from './prompts/draft_rationale';
import type {
  ClassifierResult,
  ItemEvidence,
  PatternFinding,
  PatternRunResult,
} from '../patterns/types';
import type { ChecklistGuideline, Severity } from '../checklists/types';
import { loadGuidelineById } from '../checklists/loader';
import { llmMethodologyFindingId, llmSpinFindingId } from '../patterns/finding-id';
import type { ManuscriptDocument } from '../pdf/types';
import type { DecisionLabel } from '../export/types';

export const LOW_CONFIDENCE_THRESHOLD = 0.75;

export type LlmItemStatus = 'reported' | 'partial' | 'missing' | 'not_applicable';

export interface LlmItemGrade {
  itemId: string;
  status: LlmItemStatus;
  evidenceSpan: string;
  rationale: string;
  promptVersion: string;
}

export interface LlmEnrichmentResult {
  /** Refined classifier output when the LLM disambiguated; null otherwise. */
  classifier: ClassifierResult | null;
  /** Per-item grades — only items where the LLM was consulted. */
  itemGrades: LlmItemGrade[];
  /** Findings to merge into the pattern findings array (spin etc.). */
  extraFindings: PatternFinding[];
  /** Engine metadata for audit log. */
  modelId: string;
  errors: string[];
}

const ITEM_BUDGET = 8; // cap LLM item-judgment calls per skim

function isClientReady(client: LlmClient): boolean {
  return client.getState().kind === 'ready';
}

function modelIdOf(client: LlmClient): string {
  const state = client.getState();
  return state.kind === 'ready' || state.kind === 'loading' || state.kind === 'idle' || state.kind === 'error'
    ? state.modelId
    : 'unknown';
}

async function maybeReclassify(
  client: LlmClient,
  doc: ManuscriptDocument,
  pattern: ClassifierResult,
  errors: string[],
  tick?: () => void,
): Promise<ClassifierResult | null> {
  if (pattern.confidence >= LOW_CONFIDENCE_THRESHOLD) return null;
  if (pattern.label === 'unclassified') {
    // Even when pattern can't pick, give the LLM a shot.
  }
  const req = classifyStudy.buildPrompt(doc, pattern);
  const result = await callWithSchema(client, req, classifyStudy.parseResponse);
  tick?.();
  if (!result.ok || !result.data) {
    errors.push(`classify_study failed after ${result.attempts} attempts: ${result.errors?.join('; ') ?? ''}`);
    return null;
  }
  return {
    label: result.data.label,
    confidence: result.data.confidence,
    alternatives: pattern.alternatives,
    noFormalGuideline: pattern.noFormalGuideline,
    rationale: result.data.rationale,
  };
}

function severityFromStatus(status: LlmItemStatus, baseline: Severity): Severity {
  switch (status) {
    case 'reported':
      return 'info';
    case 'partial':
      return 'amber';
    case 'missing':
      return baseline;
    case 'not_applicable':
      return 'info';
  }
}

async function judgeItems(
  client: LlmClient,
  doc: ManuscriptDocument,
  guideline: ChecklistGuideline,
  items: ItemEvidence[],
  errors: string[],
  tick?: () => void,
): Promise<{ grades: LlmItemGrade[]; findings: PatternFinding[] }> {
  const grades: LlmItemGrade[] = [];
  const findings: PatternFinding[] = [];

  // Build a fast lookup from item id → declared ChecklistItem.
  const declared = new Map<string, { item: import('../checklists/types').ChecklistItem; severityIfMissing: Severity }>();
  for (const section of guideline.sections) {
    for (const item of section.items) {
      declared.set(item.id, { item, severityIfMissing: item.severity_if_missing });
    }
  }

  // Priority: not-located high-severity first, then not-located amber, then located high-severity.
  const prioritised = [...items].sort((a, b) => {
    const score = (i: ItemEvidence) => {
      if (i.status === 'not_located' && i.severityIfMissing === 'red') return 4;
      if (i.status === 'not_located' && i.severityIfMissing === 'amber') return 3;
      if (i.status === 'located' && i.severityIfMissing === 'red') return 2;
      if (i.status === 'located') return 1;
      return 0;
    };
    return score(b) - score(a);
  });

  for (const ev of prioritised.slice(0, ITEM_BUDGET)) {
    const declaredEntry = declared.get(ev.itemId);
    if (!declaredEntry) continue;
    const req = judgeChecklistItem.buildPrompt(doc, declaredEntry.item, ev, guideline.name);
    const result = await callWithSchema(client, req, judgeChecklistItem.parseResponse);
    tick?.();
    if (!result.ok || !result.data) {
      errors.push(`judge_checklist_item ${ev.itemId} failed: ${result.errors?.join('; ') ?? ''}`);
      continue;
    }
    const data = result.data;
    grades.push({
      itemId: ev.itemId,
      status: data.status,
      evidenceSpan: data.evidence_span,
      rationale: data.rationale,
      promptVersion: result.promptVersion,
    });
    findings.push({
      id: llmMethodologyFindingId(ev.itemId, data.status),
      category: 'methodology',
      severity: severityFromStatus(data.status, declaredEntry.severityIfMissing),
      checklistItemId: ev.itemId,
      findingText: `${ev.itemId} ${data.status.replace('_', ' ')}: ${data.rationale}`,
      evidencePassage: data.evidence_span,
      evidenceOffset: ev.matches[0]?.offset ?? -1,
      section: ev.sectionHint ?? 'unknown',
      layer: 'pattern', // SkimFindingRecord.layer is constrained; we re-tag at persistence time.
    });
  }
  return { grades, findings };
}

async function classifySpinPass(
  client: LlmClient,
  doc: ManuscriptDocument,
  errors: string[],
  tick?: () => void,
): Promise<PatternFinding | null> {
  // Only run spin classification if there's an abstract and a results section.
  const hasAbstract = doc.sections.some((s) => s.section === 'abstract');
  const hasResults = doc.sections.some((s) => s.section === 'results');
  if (!hasAbstract || !hasResults) return null;
  const req = classifySpin.buildPrompt(doc);
  const result = await callWithSchema(client, req, classifySpin.parseResponse);
  tick?.();
  if (!result.ok || !result.data) {
    errors.push(`classify_spin failed: ${result.errors?.join('; ') ?? ''}`);
    return null;
  }
  if (result.data.level === 'none') return null;
  const severity: Severity =
    result.data.level === 'severe' ? 'red' : result.data.level === 'moderate' ? 'amber' : 'info';
  return {
    id: llmSpinFindingId(result.data.level),
    category: 'stats',
    severity,
    checklistItemId: null,
    findingText: `Spin (${result.data.level}): ${result.data.rationale}`,
    evidencePassage: result.data.examples[0] ?? '',
    evidenceOffset: -1,
    section: 'abstract',
    layer: 'pattern',
  };
}

export interface EnrichArgs {
  client: LlmClient;
  doc: ManuscriptDocument;
  pattern: PatternRunResult;
  /**
   * Called as each LLM call in the pass completes. `total` is an upper-bound
   * estimate (reclassify + judged items + spin) computed before the run; the
   * final call is always `(total, total)` so a progress bar reaches 100%
   * even if a phase is skipped (e.g. guideline changes after reclassify).
   */
  onProgress?: (done: number, total: number) => void;
}

export async function runLlmEnrichment(args: EnrichArgs): Promise<LlmEnrichmentResult | null> {
  if (!isClientReady(args.client)) return null;

  const errors: string[] = [];
  const modelId = modelIdOf(args.client);

  // Estimate the number of LLM calls up front so the UI can show a real
  // determinate progress bar (cloud mode is paced/slow — feedback matters).
  const willReclassify = args.pattern.classifier.confidence < LOW_CONFIDENCE_THRESHOLD;
  const willJudge =
    !args.pattern.classifier.noFormalGuideline &&
    args.pattern.classifier.label !== 'unclassified' &&
    !!args.pattern.methodology;
  const judgeCount = willJudge
    ? Math.min(ITEM_BUDGET, args.pattern.methodology!.items.length)
    : 0;
  const willSpin =
    args.doc.sections.some((s) => s.section === 'abstract') &&
    args.doc.sections.some((s) => s.section === 'results');
  const total = Math.max(1, (willReclassify ? 1 : 0) + judgeCount + (willSpin ? 1 : 0));
  let done = 0;
  const tick = () => {
    done = Math.min(done + 1, total);
    args.onProgress?.(done, total);
  };
  args.onProgress?.(0, total);

  const refinedClassifier = await maybeReclassify(
    args.client,
    args.doc,
    args.pattern.classifier,
    errors,
    tick,
  );
  const effectiveClassifier = refinedClassifier ?? args.pattern.classifier;

  let itemGrades: LlmItemGrade[] = [];
  let methodFindings: PatternFinding[] = [];
  if (
    !effectiveClassifier.noFormalGuideline &&
    effectiveClassifier.label !== 'unclassified' &&
    args.pattern.methodology
  ) {
    // If the classifier changed, route to the (possibly different) guideline.
    const guideline =
      refinedClassifier && refinedClassifier.label !== args.pattern.classifier.label
        ? null
        : loadGuidelineById(args.pattern.methodology.guidelineId);
    if (guideline) {
      const r = await judgeItems(
        args.client,
        args.doc,
        guideline,
        args.pattern.methodology.items,
        errors,
        tick,
      );
      itemGrades = r.grades;
      methodFindings = r.findings;
    }
  }

  const spinFinding = await classifySpinPass(args.client, args.doc, errors, tick);
  const extraFindings = [...methodFindings];
  if (spinFinding) extraFindings.push(spinFinding);

  // Always finish at 100% even if a phase was skipped after estimation.
  args.onProgress?.(total, total);

  return {
    classifier: refinedClassifier,
    itemGrades,
    extraFindings,
    modelId,
    errors,
  };
}

/** On-demand rationale draft (DecisionCard button). */
export async function draftDecisionRationale(
  client: LlmClient,
  doc: ManuscriptDocument,
  pattern: PatternRunResult,
  decision: DecisionLabel,
): Promise<{ ok: true; rationale: string; promptVersion: string } | { ok: false; error: string }> {
  if (!isClientReady(client)) {
    return { ok: false, error: 'LLM not loaded; load it from Settings before drafting.' };
  }
  const req = draftRationale.buildPrompt({ manuscript: doc, pattern, decision });
  const result = await callWithSchema(client, req, draftRationale.parseResponse);
  if (!result.ok || !result.data) {
    return { ok: false, error: result.errors?.join('; ') ?? 'unknown error' };
  }
  return { ok: true, rationale: result.data.rationale, promptVersion: result.promptVersion };
}
